import { FC } from 'react';
import { ResponsiveRadar } from '@nivo/radar';
import { animated } from '@react-spring/web';

import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { getBottomLegend, getChartFormatting, humanizeChartLabel, truncateChartLabel } from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

const data = [
    {
        axis: 'fruity',
        chardonay: 47,
        carmenere: 34,
        syrah: 63,
    },
    {
        axis: 'bitter',
        chardonay: 57,
        carmenere: 113,
        syrah: 96,
    },
    {
        axis: 'heavy',
        chardonay: 32,
        carmenere: 39,
        syrah: 56,
    },
    {
        axis: 'strong',
        chardonay: 36,
        carmenere: 50,
        syrah: 42,
    },
    {
        axis: 'sunny',
        chardonay: 82,
        carmenere: 61,
        syrah: 45,
    },
];

interface RadarChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const RadarChart: FC<RadarChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);
    const chartData = useNivoReportData(component, data);
    const { formatMetricValue } = useNivoReportFormatting(component);
    const keys = Object.keys(chartData[0] ?? {}).filter((key) => key !== 'axis');

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveRadar
                data={chartData}
                keys={keys}
                indexBy='axis'
                valueFormat={(value) => formatMetricValue(value)}
                margin={{ top: 48, right: 96, bottom: formatting.showLegend ? 88 : 36, left: 96 }}
                borderColor={{ from: 'color' }}
                gridLabelOffset={36}
                gridLabel={({ id, anchor, animated: animatedProps }) => (
                    <animated.text
                        dominantBaseline='central'
                        textAnchor={anchor}
                        transform={animatedProps.transform}
                        style={{
                            fill: nivoChartTheme.textColor,
                            fontSize: 11,
                        }}
                    >
                        {truncateChartLabel(id, 16)}
                    </animated.text>
                )}
                dotSize={10}
                dotColor={{ theme: 'background' }}
                theme={nivoChartTheme}
                dotBorderWidth={2}
                colors={colorsArray}
                blendMode='normal'
                motionConfig='gentle'
                sliceTooltip={({ index, data: tooltipData }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(index)}
                        entries={tooltipData.map((entry) => ({
                            color: entry.color,
                            label: entry.id,
                            value: entry.value,
                        }))}
                        formatValue={formatMetricValue}
                    />
                )}
                legends={formatting.showLegend ? getBottomLegend({ translateY: 64 }) : []}
            />
        </div>
    );
};

export default RadarChart;
